/**
 * Geometrin bakom ruttkartans bågar.
 *
 * Kartan ritar fågelvägar mellan benens punkter, och paket som kör tur och retur i samma
 * korridor (Strängnäs → Linköping → Strängnäs) fick tidigare två identiska linjer ovanpå
 * varandra: returbenet, dess pilar och dess sifferbricka doldes helt av utresan.
 *
 * Lösningen är en högerregel: varje ben böjer av åt HÖGER om sin egen färdriktning, som
 * högertrafik. A→B och B→A hamnar därmed på var sin sida av korridoren utan att vi behöver
 * leta upp dubbletter, och körriktningen går att läsa på bågen även innan man hittar pilarna.
 */

export interface LatLng {
  lat: number;
  lng: number;
}

/** Sagitta som andel av benets längd. Andel, inte pixlar ⇒ bågen ser likadan ut på alla zoomnivåer. */
export const ARC_BULGE = 0.09;

/**
 * Ben som är ensamt i sin korridor ritas nästan rakt — bågen behövs bara när något annat
 * delar sträckan, och ett enkelriktat paket ska se ut som det alltid har gjort.
 */
export const SOLO_ARC_FACTOR = 0.18;

/** Punkter per båge. 32 steg räcker för att kurvan ska se mjuk ut även helt inzoomad. */
const ARC_STEPS = 32;

/** Två punkter inom ~100 m räknas som samma ort — annars grupperas inte ben som delar korridor. */
const KEY_DECIMALS = 3;

const pointKey = (point: LatLng): string =>
  `${point.lat.toFixed(KEY_DECIMALS)},${point.lng.toFixed(KEY_DECIMALS)}`;

/** Korridoren mellan två punkter, riktningslöst: A→B och B→A får samma nyckel. */
const corridorKey = (segment: ArcSegment): string =>
  [pointKey(segment.from), pointKey(segment.to)].sort().join('|');

/** Riktad nyckel: två ben åt samma håll i samma korridor behöver olika bågdjup. */
const directionKey = (segment: ArcSegment): string =>
  `${pointKey(segment.from)}>${pointKey(segment.to)}`;

export interface ArcSegment {
  from: LatLng;
  to: LatLng;
}

/**
 * Bågdjup per segment, beräknat över ALLA sträckor som ritas samtidigt — paketets ben,
 * det valda lucka-alternativet och skiftespartnerns rutt. Ett lucka-ben som går tillbaka
 * längs paketets egen sträcka separeras därmed på samma villkor som ett returben.
 */
export const arcBulges = (segments: ArcSegment[]): number[] => {
  const corridors = new Map<string, number[]>();
  segments.forEach((segment, index) => {
    const key = corridorKey(segment);
    const existing = corridors.get(key);
    if (existing) existing.push(index);
    else corridors.set(key, [index]);
  });

  const bulges = segments.map(() => 0);

  corridors.forEach((indexes) => {
    const alone = indexes.length === 1;
    const seenPerDirection = new Map<string, number>();

    indexes.forEach((index) => {
      if (alone) {
        bulges[index] = ARC_BULGE * SOLO_ARC_FACTOR;
        return;
      }

      // Tredje benet i samma korridor går åt ett håll som redan har en båge — då räcker
      // inte höger/vänster, utan bågen måste växa för att inte hamna ovanpå den förra.
      const key = directionKey(segments[index]);
      const repeat = seenPerDirection.get(key) ?? 0;
      seenPerDirection.set(key, repeat + 1);
      bulges[index] = ARC_BULGE * (1 + repeat * 1.1);
    });
  });

  return bulges;
};

/**
 * Bågformad path mellan två punkter, böjd åt höger om färdriktningen. Returnerar en rak
 * tvåpunktslinje när bågdjupet är noll eller punkterna sammanfaller.
 */
export const arcPath = (from: LatLng, to: LatLng, bulge: number): LatLng[] => {
  if (bulge === 0) return [from, to];

  // Mercator: en longitudgrad är kortare än en latitudgrad på våra breddgrader. Utan
  // korrigeringen blir bågen snedvriden på nord-sydliga ben som Strängnäs–Linköping.
  const latK = Math.cos((((from.lat + to.lat) / 2) * Math.PI) / 180);
  const dx = (to.lng - from.lng) * latK;
  const dy = to.lat - from.lat;
  const length = Math.hypot(dx, dy);
  if (length === 0) return [from, to];

  // Högernormal i kartplanet (x = öst, y = nord) är (dy, −dx). Styrpunkten läggs på dubbla
  // sagittan, eftersom en kvadratisk bézier når halvvägs dit på sin mittpunkt.
  const reach = length * bulge * 2;
  const control = {
    lat: (from.lat + to.lat) / 2 + (-dx / length) * reach,
    lng: (from.lng + to.lng) / 2 + ((dy / length) * reach) / latK,
  };

  return Array.from({ length: ARC_STEPS + 1 }, (_, step) => {
    const t = step / ARC_STEPS;
    const u = 1 - t;
    return {
      lat: u * u * from.lat + 2 * u * t * control.lat + t * t * to.lat,
      lng: u * u * from.lng + 2 * u * t * control.lng + t * t * to.lng,
    };
  });
};

/** Bågens krön — där bennumret ska sitta, i stället för på benets råa mittpunkt. */
export const arcApex = (arc: LatLng[]): LatLng => arc[Math.floor(arc.length / 2)];
